import React, { useState, useEffect } from "react";
import * as S from "../styled";
import { Input } from "../../index-styled";
import { phoneEmailSchema as validationSchema } from "@/validation/register";
import { AbstractField, AbstractControlledField } from "_c/form/Field";
import { useForm, FormContext } from "react-hook-form";
import PT from "prop-types";
import { Button } from "_c/button";
import { UserAPI } from "@/api/user";
import { useNotify } from "@/hooks/useNotify";
import Socials from "../../socials";
import styled from "styled-components";
import PhoneEmailToggler from "../../PhoneEmailToggler";
import PhoneInput from "_c/form/PhoneInput";
import { analyticsAction } from "@/utils/analytics";
import { FEAT_FLAG_EMAIL_REG } from "@/misc/featureFlags";
import CommonAgreement from "_c/atoms/CommonAgreement";
import { useTimezone } from "@/hooks/useTimezone";

const SocialsCta = styled.p`
  color: var(--font-color-main);
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 500;
`;

const RegisterFormEmail = (props) => {
  useEffect(() => {
    analyticsAction({
      category: "reg",
      action: "pro_chosen",
    });
  }, []);
  const [isUserAgreementAccepted, setIsUserAgreementAccepted] = useState(
    props.isUserAgreementAccepted,
  );
  const timezone = useTimezone();

  const methods = useForm({
    validationSchema,
    defaultValues: props.defaultValues,
  });
  const { notifyError } = useNotify();
  const { handleSubmit, setError, formState } = methods;
  const { isSubmitting } = formState;
  const changeStrategy = (strategy) => {
    analyticsAction({
      category: "login",
      action: `auth_with_${strategy}`,
    });
    props.changeStrategy(strategy);
  };
  useEffect(() => {
    methods.register("strategy");
  }, []);
  useEffect(() => {
    methods.setValue("strategy", props.strategy);
  }, [props.strategy]);

  const onSubmit = async (baseForm) => {
    const form = {
      ...baseForm,
      is_user_agreement_accepted: isUserAgreementAccepted,
    };
    analyticsAction({
      category: "login",
      action: "log_step_1",
    });
    if (props.strategy === "email") {
      try {
        const { data: exists } = await UserAPI.isEmailExists(
          form.email.toLowerCase().trim(),
        );
        if (exists) {
          setError("email", "serverError", "email_exists");
        } else {
          props.onSubmit(form);
        }
      } catch (e) {
        notifyError(props.t([`errors:${e}`, "errors:unspecified_server"]));
      }
    } else if (props.strategy === "phone") {
      try {
        const { data: exists } = await UserAPI.isPhoneExists(form.phone.trim());
        if (exists) {
          setError("phone", "serverError", "PHONE_EXISTS");
        } else {
          props.onSubmit(form);
        }
      } catch (e) {
        notifyError(props.t([`errors:${e}`, "errors:unspecified_server"]));
      }
    }
  };
  const phoneLabel = props.t("common:phone");
  return (
    <FormContext {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {FEAT_FLAG_EMAIL_REG && (
          <PhoneEmailToggler
            t={props.t}
            value={props.strategy}
            onChange={changeStrategy}
          />
        )}
        <S.InputSect>
          {props.strategy === "email" ? (
            <AbstractField
              t={props.t}
              Component={Input}
              id="reg_email"
              name="email"
              type="email"
              placeholder="E-mail"
              aria-label="E-mail"
            />
          ) : (
            <AbstractControlledField
              locale={props.locale}
              t={props.t}
              inputComponent={Input}
              Component={PhoneInput}
              id="reg_phone"
              name="phone"
              placeholder={phoneLabel}
              aria-label={phoneLabel}
            />
          )}
          <AbstractField
            t={props.t}
            Component={Input}
            id="reg_name"
            name="first_name"
            placeholder={props.t("login:name")}
            aria-label={props.t("login:name")}
          />
        </S.InputSect>
        <div>
          <Button
            css={`
              margin-right: 10px;
            `}
            small
            negative
            type="button"
            onClick={() => props.prevStep()}
          >
            {props.t("common:prev")}
          </Button>
          <Button
            disabled={isSubmitting || !isUserAgreementAccepted}
            small
            type="submit"
            greenish
          >
            {props.t("common:next")}
          </Button>
        </div>
        <CommonAgreement
          defaultChecked={isUserAgreementAccepted}
          onChange={setIsUserAgreementAccepted}
          t={props.t}
        />
        <SocialsCta
          style={{ marginBottom: "10px", fontSize: "14px", fontWeight: 500 }}
        >
          {props.t("login:social_register_opt")}
        </SocialsCta>
        <Socials
          action={"reg"}
          isMaster={props.isMaster}
          isSaloonOwner={props.isSaloonOwner}
          isClient={props.isClient}
          locale={props.locale}
          timezone={timezone}
        />
      </form>
    </FormContext>
  );
};
RegisterFormEmail.propTypes = {
  defaultValues: PT.object.isRequired,
  locale: PT.string.isRequired,
  isMaster: PT.bool,
  isClient: PT.bool,
  isSaloonOwner: PT.bool,
  strategy: PT.string.isRequired,
  isUserAgreementAccepted: PT.bool,

  changeStrategy: PT.func.isRequired,
  prevStep: PT.func.isRequired,
  t: PT.func.isRequired,
  onSubmit: PT.func.isRequired,
};
export default RegisterFormEmail;
